/** Frontend-only permission → scope grouping for the staff ACL editor. */

import { Permission } from "@/types";

export const STAFF_PERMISSION_SCOPE_KEYS = [
  "stats",
  "users",
  "activities",
  "houses",
  "storage",
  "devices",
  "readers",
  "bookings",
  "handshakes",
  "customer_support",
  "partner_support",
  "payments",
  "alerts",
  "protocols",
  "carousel",
  "agent_transcripts",
] as const;

export type StaffPermissionScopeKey =
  (typeof STAFF_PERMISSION_SCOPE_KEYS)[number];

/** Assignable permissions grouped by portal scope (no Super exclusives). */
export const SCOPE_PERMISSION_MAP: Record<
  StaffPermissionScopeKey,
  readonly Permission[]
> = {
  stats: [Permission.STATS_READ],
  users: [
    Permission.ACTIVITIES_READ_ANY,
    Permission.HOSTS_CREATE,
    Permission.HOSTS_READ_ANY,
    Permission.HOSTS_UPDATE_ANY,
    Permission.PARTNERS_READ_ANY,
    Permission.REGIONS_LIST,
    Permission.SALES_AGENTS_MANAGE_ANY,
    Permission.SALES_AGENTS_READ_ANY,
    Permission.SELLERS_CREATE,
    Permission.SELLERS_READ_ANY,
    Permission.SELLERS_UPDATE_ANY,
    Permission.STAFF_CREATE,
    Permission.USERS_ACCOUNT_STATUS_UPDATE,
    Permission.USERS_IMPERSONATE,
    Permission.USERS_READ_ANY,
    Permission.USERS_UPDATE_ANY,
  ],
  activities: [
    Permission.ACTIVITIES_CATALOG_IMPORT,
    Permission.ACTIVITIES_CONFIG_UPDATE,
    Permission.ACTIVITIES_CREATE,
    Permission.ACTIVITIES_MANAGE_ANY,
    Permission.ACTIVITIES_READ_ANY,
    Permission.ACTIVITIES_UPDATE,
    Permission.ACTIVITY_ITEMS_WRITE,
  ],
  houses: [
    Permission.HOUSES_CREATE,
    Permission.HOUSES_PRESENCES_IMPORT,
    Permission.HOUSES_PRESENCES_READ,
    Permission.HOUSES_READ,
    Permission.HOUSES_READ_ANY,
    Permission.HOUSES_UPDATE,
    Permission.HOUSES_UPDATE_ANY,
  ],
  storage: [Permission.STORAGE_DELETE, Permission.STORAGE_UPLOAD],
  devices: [
    Permission.DEVICE_PRESENCES_CREATE,
    Permission.DEVICE_PRESENCES_DELETE,
    Permission.DEVICE_PRESENCES_READ,
    Permission.DEVICE_PRESENCES_UPDATE,
    Permission.DEVICES_CREATE,
    Permission.DEVICES_HOUSE_CREATE,
    Permission.DEVICES_HOUSE_READ,
    Permission.DEVICES_HOUSE_RENAME,
    Permission.DEVICES_HOUSE_RESET_PASSWORD,
    Permission.DEVICES_HOUSE_UPDATE,
    Permission.DEVICES_PRESETS_CREATE,
    Permission.DEVICES_PRESETS_READ,
    Permission.DEVICES_PRESETS_UPDATE,
    Permission.DEVICES_READ,
    Permission.DEVICES_RESET_PASSWORD,
    Permission.DEVICES_UPDATE,
  ],
  readers: [
    Permission.READERS_CREATE,
    Permission.READERS_PAIR,
    Permission.READERS_PRESETS_CREATE,
    Permission.READERS_PRESETS_READ,
    Permission.READERS_PRESETS_UPDATE,
    Permission.READERS_READ,
    Permission.READERS_UPDATE,
  ],
  bookings: [
    Permission.BOOKINGS_AMEND_ANY,
    Permission.BOOKINGS_CANCEL_ANY,
    Permission.BOOKINGS_READ_ANY,
    Permission.BOOKINGS_UPDATE_ANY,
  ],
  handshakes: [
    Permission.HANDSHAKES_MESSAGE_SEND_ANY,
    Permission.HANDSHAKES_SESSION_CONFIRM_ANY,
    Permission.HANDSHAKES_SESSION_REOPEN,
    Permission.HANDSHAKES_SESSIONS_READ_ANY,
  ],
  customer_support: [
    Permission.CUSTOMER_SUPPORT_CHATS_CLOSE,
    Permission.CUSTOMER_SUPPORT_CHATS_MESSAGE,
    Permission.CUSTOMER_SUPPORT_CHATS_READ_ANY,
  ],
  partner_support: [
    Permission.SUPPORT_TICKETS_MESSAGE_ANY,
    Permission.SUPPORT_TICKETS_READ_ANY,
    Permission.SUPPORT_TICKETS_STATS_ANY,
    Permission.SUPPORT_TICKETS_UPDATE_ANY,
  ],
  payments: [
    Permission.BOOKINGS_REFUND_ANY,
    Permission.ECONOMIC_EVENTS_LIST,
    Permission.ECONOMIC_EVENTS_WRITE,
    Permission.EXTERNAL_PAYMENTS_LIST,
    Permission.SETTLEMENT_DOCUMENTS_READ,
    Permission.SETTLEMENTS_LIST,
    Permission.SETTLEMENTS_WRITE,
    Permission.WALLET_READ,
  ],
  alerts: [Permission.NOTIFICATIONS_READ, Permission.NOTIFICATIONS_UPDATE],
  protocols: [Permission.PROTOCOLS_READ],
  carousel: [
    Permission.CAROUSEL_SLIDES_READ,
    Permission.CAROUSEL_SLIDES_WRITE,
  ],
  agent_transcripts: [Permission.AGENT_VOICE_TRANSCRIPTS_READ],
};

/**
 * Write/create/update permissions that only make sense with a matching read.
 * Editor-only UX — backend does not enforce these edges.
 */
export const PERMISSION_REQUIRES: Partial<
  Record<Permission, readonly Permission[]>
> = {
  [Permission.ACTIVITIES_CREATE]: [Permission.ACTIVITIES_READ_ANY],
  [Permission.ACTIVITIES_UPDATE]: [Permission.ACTIVITIES_READ_ANY],
  [Permission.ACTIVITIES_MANAGE_ANY]: [Permission.ACTIVITIES_READ_ANY],
  [Permission.ACTIVITIES_CONFIG_UPDATE]: [Permission.ACTIVITIES_READ_ANY],
  [Permission.ACTIVITY_ITEMS_WRITE]: [Permission.ACTIVITIES_READ_ANY],
  [Permission.ACTIVITIES_CATALOG_IMPORT]: [Permission.ACTIVITIES_READ_ANY],

  [Permission.HOUSES_CREATE]: [Permission.HOUSES_READ_ANY],
  [Permission.HOUSES_UPDATE_ANY]: [Permission.HOUSES_READ_ANY],
  [Permission.HOUSES_UPDATE]: [Permission.HOUSES_READ],
  [Permission.HOUSES_PRESENCES_IMPORT]: [Permission.HOUSES_PRESENCES_READ],

  [Permission.DEVICES_CREATE]: [Permission.DEVICES_READ],
  [Permission.DEVICES_UPDATE]: [Permission.DEVICES_READ],
  [Permission.DEVICES_RESET_PASSWORD]: [Permission.DEVICES_READ],
  [Permission.DEVICES_PRESETS_CREATE]: [Permission.DEVICES_PRESETS_READ],
  [Permission.DEVICES_PRESETS_UPDATE]: [Permission.DEVICES_PRESETS_READ],
  [Permission.DEVICES_HOUSE_CREATE]: [Permission.DEVICES_HOUSE_READ],
  [Permission.DEVICES_HOUSE_RENAME]: [Permission.DEVICES_HOUSE_READ],
  [Permission.DEVICES_HOUSE_UPDATE]: [Permission.DEVICES_HOUSE_READ],
  [Permission.DEVICES_HOUSE_RESET_PASSWORD]: [Permission.DEVICES_HOUSE_READ],
  [Permission.DEVICE_PRESENCES_CREATE]: [Permission.DEVICE_PRESENCES_READ],
  [Permission.DEVICE_PRESENCES_UPDATE]: [Permission.DEVICE_PRESENCES_READ],
  [Permission.DEVICE_PRESENCES_DELETE]: [Permission.DEVICE_PRESENCES_READ],

  [Permission.READERS_CREATE]: [Permission.READERS_READ],
  [Permission.READERS_UPDATE]: [Permission.READERS_READ],
  [Permission.READERS_PAIR]: [Permission.READERS_READ],
  [Permission.READERS_PRESETS_CREATE]: [Permission.READERS_PRESETS_READ],
  [Permission.READERS_PRESETS_UPDATE]: [Permission.READERS_PRESETS_READ],

  [Permission.BOOKINGS_AMEND_ANY]: [Permission.BOOKINGS_READ_ANY],
  [Permission.BOOKINGS_CANCEL_ANY]: [Permission.BOOKINGS_READ_ANY],
  [Permission.BOOKINGS_UPDATE_ANY]: [Permission.BOOKINGS_READ_ANY],
  [Permission.BOOKINGS_REFUND_ANY]: [Permission.BOOKINGS_READ_ANY],

  [Permission.HANDSHAKES_MESSAGE_SEND_ANY]: [
    Permission.HANDSHAKES_SESSIONS_READ_ANY,
  ],
  [Permission.HANDSHAKES_SESSION_CONFIRM_ANY]: [
    Permission.HANDSHAKES_SESSIONS_READ_ANY,
  ],
  [Permission.HANDSHAKES_SESSION_REOPEN]: [
    Permission.HANDSHAKES_SESSIONS_READ_ANY,
  ],

  [Permission.CUSTOMER_SUPPORT_CHATS_CLOSE]: [
    Permission.CUSTOMER_SUPPORT_CHATS_READ_ANY,
  ],
  [Permission.CUSTOMER_SUPPORT_CHATS_MESSAGE]: [
    Permission.CUSTOMER_SUPPORT_CHATS_READ_ANY,
  ],

  [Permission.SUPPORT_TICKETS_MESSAGE_ANY]: [
    Permission.SUPPORT_TICKETS_READ_ANY,
  ],
  [Permission.SUPPORT_TICKETS_UPDATE_ANY]: [Permission.SUPPORT_TICKETS_READ_ANY],
  [Permission.SUPPORT_TICKETS_STATS_ANY]: [Permission.SUPPORT_TICKETS_READ_ANY],

  [Permission.SELLERS_CREATE]: [Permission.SELLERS_READ_ANY],
  [Permission.SELLERS_UPDATE_ANY]: [Permission.SELLERS_READ_ANY],
  [Permission.HOSTS_CREATE]: [Permission.HOSTS_READ_ANY],
  [Permission.HOSTS_UPDATE_ANY]: [Permission.HOSTS_READ_ANY],
  [Permission.SALES_AGENTS_MANAGE_ANY]: [Permission.SALES_AGENTS_READ_ANY],
  [Permission.USERS_UPDATE_ANY]: [Permission.USERS_READ_ANY],
  [Permission.USERS_IMPERSONATE]: [Permission.USERS_READ_ANY],
  [Permission.USERS_ACCOUNT_STATUS_UPDATE]: [Permission.USERS_READ_ANY],
  [Permission.STAFF_CREATE]: [Permission.USERS_READ_ANY],

  [Permission.SETTLEMENTS_WRITE]: [Permission.SETTLEMENTS_LIST],
  [Permission.ECONOMIC_EVENTS_WRITE]: [Permission.ECONOMIC_EVENTS_LIST],
  [Permission.NOTIFICATIONS_UPDATE]: [Permission.NOTIFICATIONS_READ],
  [Permission.STORAGE_DELETE]: [Permission.STORAGE_UPLOAD],
  [Permission.CAROUSEL_SLIDES_WRITE]: [Permission.CAROUSEL_SLIDES_READ],
};

/** All permissions that can be toggled in the editor (no exclusives). */
export const ASSIGNABLE_STAFF_PERMISSIONS: readonly Permission[] = Array.from(
  new Set(
    STAFF_PERMISSION_SCOPE_KEYS.flatMap((scope) => [
      ...SCOPE_PERMISSION_MAP[scope],
    ]),
  ),
).sort();

export function missingPermissionRequirements(
  permission: Permission,
  selected: ReadonlySet<Permission>,
): readonly Permission[] {
  const requires = PERMISSION_REQUIRES[permission];
  if (!requires) return [];
  return requires.filter((required) => !selected.has(required));
}

function cascadeRemoveDependents(
  selected: Set<Permission>,
  removed: Permission,
): void {
  for (const [dependent, requires] of Object.entries(PERMISSION_REQUIRES)) {
    if (!requires?.includes(removed)) continue;
    if (!selected.has(dependent as Permission)) continue;
    selected.delete(dependent as Permission);
    cascadeRemoveDependents(selected, dependent as Permission);
  }
}

export function togglePermission(
  permissions: ReadonlySet<Permission>,
  permission: Permission,
  enabled: boolean,
): Set<Permission> {
  const next = new Set(permissions);
  if (enabled) {
    if (missingPermissionRequirements(permission, next).length > 0) return next;
    next.add(permission);
    return next;
  }
  next.delete(permission);
  cascadeRemoveDependents(next, permission);
  return next;
}

export interface PermissionEntry {
  permission: Permission;
  action: string;
}

/** `readers:presets:create` → resource `readers:presets`, action `create`. */
export function groupByResource(
  scopePermissions: readonly Permission[],
): { resource: string; entries: PermissionEntry[] }[] {
  const groups = new Map<string, PermissionEntry[]>();
  for (const permission of scopePermissions) {
    const separator = permission.lastIndexOf(":");
    const resource =
      separator === -1 ? permission : permission.slice(0, separator);
    const action =
      separator === -1 ? permission : permission.slice(separator + 1);
    const entries = groups.get(resource) ?? [];
    entries.push({ permission, action });
    groups.set(resource, entries);
  }
  return [...groups.entries()].map(([resource, entries]) => ({
    resource,
    entries,
  }));
}
